/** @jsx jsx */
import { Flex, FormAssistive, FormGroup, FormLabel, InformationIcon, Paragraph, Radio } from "@eworkit/iris";
import { FormikProps } from "formik";
import { FC, memo } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { FormikTextArea, VALIDATION_TYPES } from "@/components/Formik";
import { FormikError } from "@/components/Formik";
import { ELABORATION_MAX_LENGTH } from "@/data/job-applications";
import { Answer } from "@/data/questionnaire/types/Answer";
import { Collapsible } from "@/eworkitComponents";

export interface AnswerFormValues {
  answers?: Answer[];
}

interface QuestionProps {
  answer: Answer;
  name: string;
  form: FormikProps<AnswerFormValues>;
}

export const ApplyQuestion: FC<QuestionProps> = memo(({ answer, name, form }) => {
  const { t } = useTranslation("session");

  const elaborationCharsLeft = ELABORATION_MAX_LENGTH - (answer?.elaboration?.length ?? 0);

  return (
    <Flex flexDirection="column" gap="sm">
      <Flex>
        <FormGroup
          label={<FormLabel color="n700" label={answer.question.question} required={answer.question.mandatory} />}
          error={
            <FormikError
              name={`${name}.answer`}
              overrideMessages={{
                [VALIDATION_TYPES.REQUIRED]: t(
                  "JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.QUESTIONNAIRE.MANDATORY_QUESTION",
                ),
              }}
            />
          }>
          <Flex flexDirection="column" flexGrow={1}>
            <Flex flexDirection="column" gap="xs">
              <Flex alignItems="center" gap="xs">
                <Radio
                  name={`${answer.question.id}.question`}
                  onChange={() => form.setFieldValue(`${name}.answer`, true, true)}
                  checked={answer.answer === true}
                />
                <Paragraph size="p-md">
                  {t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.QUESTIONNAIRE.YES_ANSWER")}
                </Paragraph>
              </Flex>

              <Flex alignItems="center" gap="xs">
                <Radio
                  name={`${answer.question.id}.question`}
                  onChange={() => form.setFieldValue(`${name}.answer`, false, true)}
                  checked={answer.answer === false}
                />
                <Paragraph size="p-md">
                  {t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.QUESTIONNAIRE.NO_ANSWER")}
                </Paragraph>
              </Flex>
            </Flex>
          </Flex>
        </FormGroup>
      </Flex>

      <Flex flexDirection="column" flexGrow={1}>
        <Collapsible collapsed={answer.answer === null}>
          <FormGroup
            error={
              <FormikError
                name={`${name}.elaboration`}
                overrideMessages={{
                  [VALIDATION_TYPES.MAX_LENGTH]: t(
                    "JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.QUESTIONNAIRE.ELABORATION_MAX_LENGTH",
                  ),
                }}
              />
            }
            assistive={
              elaborationCharsLeft >= 0 ? (
                <FormAssistive
                  icon={<InformationIcon color="n700" />}
                  text={t("JOB_REQUESTS.APPLY.WIZARD.CHARACTERS_REMAINING", {
                    chars: elaborationCharsLeft,
                  })}
                />
              ) : undefined
            }>
            <FormikTextArea
              name={`${name}.elaboration`}
              value={answer.elaboration ?? undefined}
              placeholder={t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.QUESTIONNAIRE.OPEN_QUESTION_PLACEHOLDER")}
              sx={{ minHeight: 90 }}
            />
          </FormGroup>
        </Collapsible>
      </Flex>
    </Flex>
  );
});
